import React, { useState, useEffect, useMemo, useRef } from 'react';
import FirmsTable from './firmsTable';
import {
  Box, Typography, Grid, TextField, useMediaQuery, Divider, IconButton, Container
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ThemeProvider } from '@mui/material/styles';
//import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
//import { useAuth } from '../../context/AuthContext';
//import { DEFAULT_REDIRECT_PATH, resolvePostLoginDestination } from '../../utils/navigation';
import theme from '../../styles/StyleTheme';
import buildingsBackground from '../../assets/images/fondo-edificiospng.png';
import Loading from '../../components/Loading';
import Pagination from '@mui/material/Pagination';
import { publicAxios } from '../../apiClients';
import SelectOptionMM from '../../components/SelectOptionMM';
import MMLogo from '../../assets/banners/MM-blanco sin fondo.png';
import Button from '@mui/material/Button';
import InstitutionButton from '../../components/InstitutionButton';
import { generateFirmRouteSegments } from '../../utils/slugify';
import { createFirmsFuse, searchFirms } from '../../utils/marketMonitorLegalSearch';

const extractImageUrl = (imageRecord) => {
  if (!imageRecord) return null;
  if (typeof imageRecord === 'string') return imageRecord;
  return (
    imageRecord.imageUrl ??
    imageRecord.url ??
    imageRecord.image ??
    imageRecord?.image?.imageUrl ??
    imageRecord?.image?.url ??
    null
  );
};

// ✅ helper: parse de fecha seguro (para fecha_vencimiento)
const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const isoDate = new Date(trimmed);
    if (!Number.isNaN(isoDate.getTime())) return isoDate;

    const matchDmY = trimmed.match(
      /^(\d{2})[\/-](\d{2})[\/-](\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (matchDmY) {
      const [, day, month, year, hour = '0', minute = '0', second = '0'] = matchDmY;
      const d = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const matchYmD = trimmed.match(
      /^(\d{4})[\/-](\d{2})[\/-](\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (matchYmD) {
      const [, year, month, day, hour = '0', minute = '0', second = '0'] = matchYmD;
      const d = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
};

const parsePagoFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['si', 'sí', 'yes', 'true', '1', 'y'].includes(normalized)) return true;
    if (['no', 'false', '0', 'n'].includes(normalized)) return false;
  }
  return false;
};

const parsePosicionPago = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && [1, 2, 3].includes(parsed) ? parsed : null;
};


// ✅ Lazy-load cards (image + content) when they enter viewport
const useLazyCardContent = (imageUrl) => {
  const cardRef = useRef(null);
  const [resolvedImageUrl, setResolvedImageUrl] = useState(null);
  const [isContentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    setResolvedImageUrl(null);
    setContentVisible(false);

    const node = cardRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setResolvedImageUrl(imageUrl ?? null);
      setContentVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (imageUrl) setResolvedImageUrl(imageUrl);
        setContentVisible(true);
        observer.disconnect();
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(node);

    return () => {
      try {
        observer.unobserve(node);
      } catch { }
      observer.disconnect();
    };
  }, [imageUrl]);

  return { cardRef, resolvedImageUrl, isContentVisible };
};

const ArticleCard = ({ article, variant = 'vertical', compactCardsPerView }) => {
  const isCompact = variant === 'compact';
  const { cardRef, resolvedImageUrl, isContentVisible } = useLazyCardContent(article.imageUrl);

  const gapXsPx = 12;
  const gapSmPx = 16;
  const cardWidthXs = `calc((100% - ${(compactCardsPerView - 1) * gapXsPx}px) / ${compactCardsPerView})`;
  const cardWidthSm = `calc((100% - ${(compactCardsPerView - 1) * gapSmPx}px) / ${compactCardsPerView})`;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: 'relative',
        borderRadius: { xs: 2.5, sm: 3, md: 4 },
        overflow: 'hidden',
        backgroundColor: 'rgba(3, 38, 61, 0.35)',
        backgroundImage: resolvedImageUrl ? `url(${resolvedImageUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        ...(isCompact
          ? {
            flex: {
              xs: `0 0 ${cardWidthXs}`,
              sm: `0 0 ${cardWidthSm}`,
              md: '0 0 auto',
            },
            width: {
              xs: cardWidthXs,
              sm: cardWidthSm,
              md: 240,
            },
            aspectRatio: { xs: '1 / 1', sm: '1 / 1', md: '1 / 1' },
            height: 'auto',
            minWidth: 0,
            borderRadius: { xs: 2, sm: 3, md: 4 },
            overflow: 'hidden',
          }
          : {
            width: 240,
            height: 240,
            flex: '0 0 auto',
            minWidth: 0,
          }),
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(3, 38, 61, 0.55)',
          zIndex: 0,
        }}
      />

      {/* Contenido central */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isCompact ? { xs: 0.6, sm: 0.8 } : 1.25,
          px: isCompact ? { xs: 1, sm: 1.5 } : 2.5,
          pb: isCompact ? { xs: 5.5, sm: 6.5 } : 9,
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        {isContentVisible && (
          <>
            {article.codigo || article.title ? (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: '#fff',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontSize: isCompact ? { xs: '0.78rem', sm: '0.9rem' } : { xs: '1.05rem', md: '1.18rem' },
                  lineHeight: 1.15,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {article.codigo || article.title}
              </Typography>
            ) : null}

            {article.year ? (
              <Typography
                variant="body2"
                sx={{
                  color: '#E0ECF6',
                  fontSize: isCompact ? { xs: '0.68rem', sm: '0.78rem' } : { xs: '0.95rem', md: '1rem' },
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                {article.year}
              </Typography>
            ) : null}

            {article.area ? (
              <Typography
                variant="body2"
                sx={{
                  color: '#D6E4F0',
                  fontSize: isCompact ? { xs: '0.68rem', sm: '0.78rem' } : { xs: '0.88rem', md: '0.95rem' },
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {article.area}
              </Typography>
            ) : null}
          </>
        )}
      </Box>

      {/* === Bloque inferior (firma + botón) === */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isCompact ? { xs: 0.5, sm: 0.6 } : 0.9,
          p: isCompact ? { xs: 0.9, sm: 1.1 } : 2,
          zIndex: 2,
          background:
            'linear-gradient(180deg, rgba(3,38,61,0) 0%, rgba(3,38,61,0.78) 55%, rgba(3,38,61,0.92) 100%)',
        }}
      >
        {isContentVisible && (
          <>
            <Box
              sx={{
                display: { xs: 'none', sm: 'none', md: 'flex' },
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  color: 'rgba(255,255,255,0.9)',
                  textAlign: 'center',
                  fontSize: isCompact ? { xs: '0.66rem', sm: '0.7rem' } : '0.75rem',
                  maxWidth: '95%',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {article.firmName}
              </Typography>
            </Box>

            <Button
              component="a"
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              sx={{
                backgroundColor: '#3592D2',
                color: '#fff',
                textTransform: 'none',
                fontSize: isCompact ? { xs: '0.7rem', sm: '0.76rem' } : { xs: '0.8rem', md: '0.82rem' },
                px: isCompact ? { xs: 1.4, sm: 1.8 } : { xs: 2, md: 3 },
                py: isCompact ? { xs: 0.35, sm: 0.45 } : { xs: 0.6, md: 1 },
                minHeight: { xs: 28, sm: 30 },
                borderRadius: { xs: 8, sm: 10, md: 20 },
                boxShadow: '0px 6px 20px rgba(0,0,0,0.18)',
                maxWidth: '95%',
                '&:hover': { backgroundColor: '#2878A8' },
              }}
            >
              Read more
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

const MarketMonitorLegal = () => {
  //const navigate = useNavigate();
  //const location = useLocation();
  //const { isAuthenticated, tokens } = useAuth();

  {/* const handleSubmitBackgroundClick = () => {
    if (!isAuthenticated || !tokens) return;
    const destination = resolvePostLoginDestination({
      sessionTokens: tokens,
      fromRoute: location?.pathname,
      fallbackPath: DEFAULT_REDIRECT_PATH,
    });
    navigate(destination, { replace: true, state: { from: location } });
  }; */}

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [firms, setFirms] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState({ field: 'position', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [articleStartIndex, setArticleStartIndex] = useState(0);

  // Altura tabla ↔ sidebar (solo desktop)
  const tableRef = useRef(null);
  const [sidebarHeight, setSidebarHeight] = useState(null);

  const firmsPerPage = 30;

  const isNarrow = useMediaQuery('(max-width:1010px)');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1100px)');
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const showCompactCarousel = !isDesktop;
  const compactCardsPerView = isMobile ? 2 : 3;

  const sectionWidth = { xs: '100%', md: '90%', lg: '80%' };
  const CONTENT_MAX_PX = 1400;

  // Fuse.js
  const fuse = useMemo(() => createFirmsFuse(firms), [firms]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1) Trae el listado
        const response = await publicAxios.get('/institution/market-monitor-legal/full-data');
        const data = response.data;

        // 2) Pide detalle solo de firmas con artículos (puedes cambiar a "data" si quieres todas)
        const firmsToDetail = data.filter((f) => {
          const hasArticles = Array.isArray(f.articles) && f.articles.length > 0;
          const arenaValue = Number(f?.arena);
          const needsArenaDetail = !Number.isFinite(arenaValue);
          return hasArticles && needsArenaDetail;
        });

        // 3) De cada detalle, toma "arena" (igual que MarketFirm)
        const detailResults = await Promise.all(
          firmsToDetail.map(async (f) => {
            try {
              const det = await publicAxios.get(`/institution/market-monitor-legal/${f.id}`);
              const arenaNum = Number(det?.data?.arena);
              return { id: f.id, arena: Number.isFinite(arenaNum) ? arenaNum : Number.NEGATIVE_INFINITY };
            } catch {
              return { id: f.id, arena: Number.NEGATIVE_INFINITY };
            }
          })
        );

        // 4) Mapa id -> arena
        const arenaById = new Map(detailResults.map(d => [d.id, d.arena]));

        // 5) Agrega slugs y pega arena directo en cada firm
        const firmsWithSlugs = data.map((firm) => {
          const { firmSlug, countrySlug } = generateFirmRouteSegments({
            institutionName: firm.institutionName,
            countryName: firm.Country?.name ?? firm.country?.name,
            countryId: firm.Country?.id ?? firm.country?.id,
            firmId: firm.id,
          });

          const arenaFromDetail = arenaById.has(firm.id) ? arenaById.get(firm.id) : Number(firm?.arena);
          const safeArena = Number.isFinite(arenaFromDetail) ? arenaFromDetail : Number.NEGATIVE_INFINITY;

          return {
            ...firm,
            slug: firmSlug,
            countrySlug,
            arena: safeArena,
          };
        });

        setFirms(firmsWithSlugs);
      } catch (error) {
        console.error('Error fetching firms:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Recuperar filtros guardados
    const savedFilters = JSON.parse(localStorage.getItem('marketMonitorLegalFilters'));
    if (savedFilters) {
      setSearchTerm(savedFilters.searchTerm || '');
      setSelectedCountry(savedFilters.selectedCountry || '');
      setSelectedRegion(savedFilters.selectedRegion || '');
      setCurrentPage(savedFilters.currentPage || 1);
    }
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    saveFilters(event.target.value, selectedCountry, selectedRegion, currentPage);
  };

  const handleCountryChange = (newCountryId) => {
    setSelectedCountry(newCountryId || '');
    setSelectedRegion('');
    setCurrentPage(1);
    saveFilters(searchTerm, newCountryId, '', 1);
  };

  const handleRegionChange = (newRegionId) => {
    setSelectedRegion(newRegionId || '');
    setSelectedCountry('');
    setCurrentPage(1);
    saveFilters(searchTerm, '', newRegionId, 1);
  };

  const saveFilters = (search, country, region, page) => {
    localStorage.setItem(
      'marketMonitorLegalFilters',
      JSON.stringify({
        searchTerm: search,
        selectedCountry: country,
        selectedRegion: region,
        currentPage: page,
      })
    );
  };

  const sortFirms = (firmsArr) => {
    return [...firmsArr].sort((a, b) => {
      if (sortBy.field === 'position') {
        const positionDiff = a.position - b.position;
        if (positionDiff === 0) {
          return sortBy.direction === 'asc'
            ? a.institutionName.localeCompare(b.institutionName)
            : b.institutionName.localeCompare(a.institutionName);
        }
        return sortBy.direction === 'asc' ? positionDiff : -positionDiff;
      }

      if (sortBy.field === 'totalScore') {
        const scoreDiff = a.totalScore - b.totalScore;
        if (scoreDiff === 0) {
          return sortBy.direction === 'desc'
            ? a.institutionName.localeCompare(b.institutionName)
            : b.institutionName.localeCompare(a.institutionName);
        }
        return sortBy.direction === 'asc' ? scoreDiff : -scoreDiff;
      }

      if (sortBy.field === 'institutionName') {
        return sortBy.direction === 'asc'
          ? a.institutionName.localeCompare(b.institutionName)
          : b.institutionName.localeCompare(a.institutionName);
      }

      if (sortBy.field === 'region') {
        return sortBy.direction === 'asc'
          ? a.country.region.localeCompare(b.country.region)
          : b.country.region.localeCompare(a.country.region);
      }

      if (sortBy.field === 'country') {
        return sortBy.direction === 'asc'
          ? a.country.name.localeCompare(b.country.name)
          : b.country.name.localeCompare(a.country.name);
      }

      return 0;
    });
  };

  const uniqueRegions = [...new Set(
    firms
      .filter((firm) => firm.country && firm.country.region)
      .map((firm) => firm.country.region)
  )]
    .sort((a, b) => a.localeCompare(b))
    .map((region) => ({ id: region, name: region }));

  const uniqueCountries = selectedRegion
    ? [...new Set(
      firms
        .filter((firm) => firm.country && firm.country.region === selectedRegion)
        .map((firm) => firm.country.name)
    )]
      .sort((a, b) => a.localeCompare(b))
      .map((country) => ({ id: country, name: country }))
    : [...new Set(
      firms
        .filter((firm) => firm.country && firm.country.name)
        .map((firm) => firm.country.name)
    )]
      .sort((a, b) => a.localeCompare(b))
      .map((country) => ({ id: country, name: country }));

  // FILTRADO
  let filteredFirms = searchFirms(firms, searchTerm, fuse).filter((firm) =>
    (!selectedCountry || firm.country.name === selectedCountry) &&
    (!selectedRegion || firm.country.region === selectedRegion)
  );

  const sortedFirms = sortFirms(filteredFirms);
  const indexOfLastFirm = currentPage * firmsPerPage;
  const indexOfFirstFirm = indexOfLastFirm - firmsPerPage;
  const currentFirms = sortedFirms.slice(indexOfFirstFirm, indexOfLastFirm);

  // Normalización mínima
  const normalizedFirms = useMemo(() => {
    return filteredFirms
      .map((firm) => {
        const countryData = firm.Country ?? firm.country ?? {};
        const countryId = countryData?.id ?? countryData?.isoCode ?? countryData?.name ?? `country-${firm.id}`;
        const countryName = typeof countryData?.name === 'string' ? countryData.name : '';
        const countryImages = Array.isArray(countryData?.images)
          ? [...countryData.images].sort((a, b) => {
            const priorityA = typeof a?.priority === 'number' ? a.priority : Number.MAX_SAFE_INTEGER;
            const priorityB = typeof b?.priority === 'number' ? b.priority : Number.MAX_SAFE_INTEGER;
            return priorityA - priorityB;
          })
          : [];

        const countryImageUrls = countryImages
          .map((imageRecord) => {
            const candidate = extractImageUrl(imageRecord);
            if (typeof candidate !== 'string') return null;
            const trimmed = candidate.trim();
            return trimmed.length > 0 ? trimmed : null;
          })
          .filter((url, index, arr) => Boolean(url) && arr.indexOf(url) === index);

        const normalizedArticles = (firm.articles ?? [])
          .filter((article) => Boolean(article?.link))
          .map((article, index) => {
            const parsedDate = article?.fecha ? new Date(article.fecha).getTime() : Number.NaN;
            const fechaValue = Number.isFinite(parsedDate) ? parsedDate : null;

            return {
              id: article?.id ?? `${firm.id}-${index}`,
              link: article.link,
              codigo: article?.codigo ?? '',
              area: article?.area ?? '',
              year: article?.year ?? '',
              fecha: article?.fecha ?? null,
              fechaValue,
              title: article?.title ?? '',
              firmId: firm.id,
              firmName: firm.institutionName,
              articleImageUrl: article?.imageUrl ?? null,


              // === NUEVO (Paid articles)
              pago: parsePagoFlag(article?.pago),
              posicion_pago: parsePosicionPago(article?.posicion_pago),
              fecha_vencimiento: article?.fecha_vencimiento ?? null,
            };
          })
          .sort((a, b) => {
            if (a.fechaValue === null && b.fechaValue === null) {
              return a.title.localeCompare(b.title);
            }
            if (a.fechaValue === null) return 1;
            if (b.fechaValue === null) return -1;
            if (b.fechaValue !== a.fechaValue) {
              return b.fechaValue - a.fechaValue;
            }
            return a.title.localeCompare(b.title);
          });

        const totalScoreValue = Number.isFinite(Number(firm.totalScore)) ? Number(firm.totalScore) : 0;
        const arenaValue = Number.isFinite(Number(firm.arena)) ? Number(firm.arena) : Number.NEGATIVE_INFINITY;

        return {
          firmId: firm.id,
          firmName: firm.institutionName,
          totalScore: totalScoreValue,
          arena: arenaValue,
          fallbackImageUrl: firm.firmImageUrl ?? null,
          countryId,
          countryName,
          countryImageUrls,
          articles: normalizedArticles,
        };
      })
      .filter((entry) => entry.articles.length > 0);
  }, [filteredFirms]);

  const filteredArticles = useMemo(() => {
    if (normalizedFirms.length === 0) return [];

    const countryImageCounters = new Map();

    const getNextCountryImageUrl = (firm) => {
      const images = Array.isArray(firm.countryImageUrls) ? firm.countryImageUrls : [];
      if (images.length === 0) return firm.fallbackImageUrl ?? null;

      const countryKey =
        firm.countryId ??
        firm.countryName ??
        firm.firmId ??
        `firm-${firm.firmName ?? 'unknown-country'}`;

      const currentIndex = countryImageCounters.get(countryKey) ?? 0;
      const selectedImage = images[currentIndex % images.length];
      countryImageCounters.set(countryKey, currentIndex + 1);

      if (typeof selectedImage === 'string') {
        const trimmed = selectedImage.trim();
        if (trimmed.length > 0) return trimmed;
      }
      return firm.fallbackImageUrl ?? null;
    };

    const resolveImageUrl = (article, firm) => {
      if (article.articleImageUrl) return article.articleImageUrl;
      return getNextCountryImageUrl(firm);
    };

    // Si hay búsqueda: mostrar TODOS los artículos de las firmas que coincidan
    if (searchTerm) {
      const allArticlesFromSearch = normalizedFirms.flatMap(firm =>
        firm.articles.map(article => ({
          id: article.id,
          link: article.link,
          codigo: article.codigo,
          area: article.area,
          year: article.year,
          fecha: article.fecha,
          title: article.title,
          firmId: article.firmId,
          firmName: article.firmName,
          imageUrl: resolveImageUrl(article, firm),
          fechaValue: article.fechaValue ?? Number.NEGATIVE_INFINITY,
          arena: firm.arena,
          totalScore: firm.totalScore,
          countryName: firm.countryName,
          //pago articulo
          pago: article?.pago ?? false,
          posicion_pago: article?.posicion_pago ?? null,
          fecha_vencimiento: article?.fecha_vencimiento ?? null,
        }))
      );

      return allArticlesFromSearch.sort((a, b) => {
        if (b.fechaValue !== a.fechaValue) return b.fechaValue - a.fechaValue;
        return a.firmName.localeCompare(b.firmName);
      });
    }

    // CASO 1: Hay país seleccionado → orden por totalScore desc
    if (selectedCountry) {
      return [...normalizedFirms]
        .sort((a, b) => {
          if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore;
          const aDate = a.articles[0]?.fechaValue ?? Number.NEGATIVE_INFINITY;
          const bDate = b.articles[0]?.fechaValue ?? Number.NEGATIVE_INFINITY;
          if (bDate !== aDate) return bDate - aDate;
          if (b.arena !== a.arena) return b.arena - a.arena;
          return a.firmName.localeCompare(b.firmName);
        })
        .map((firm) => {
          const article = firm.articles[0];
          if (!article) return null;
          return {
            id: article.id,
            link: article.link,
            codigo: article.codigo,
            area: article.area,
            year: article.year,
            fecha: article.fecha,
            title: article.title,
            firmId: article.firmId,
            firmName: article.firmName,
            imageUrl: resolveImageUrl(article, firm),
            fechaValue: article.fechaValue ?? Number.NEGATIVE_INFINITY,
            arena: firm.arena,
            countryName: firm.countryName,
            // === NUEVO (Paid articles)
            pago: article?.pago ?? false,
            posicion_pago: article?.posicion_pago ?? null,
            fecha_vencimiento: article?.fecha_vencimiento ?? null,
          };
        })
        .filter(Boolean);
    }

    // CASO 2: Sin país seleccionado → 1 firma por país (mejor por totalScore, luego fecha, luego arena)
    const countryArticles = new Map();

    normalizedFirms.forEach((firm) => {
      const article = firm.articles[0];
      if (!article) return;

      const candidate = {
        firm,
        article,
        totalScore: firm.totalScore ?? Number.NEGATIVE_INFINITY,
        fechaValue: article.fechaValue ?? Number.NEGATIVE_INFINITY,
        arena: Number.isFinite(firm.arena) ? firm.arena : Number.NEGATIVE_INFINITY,
      };

      const current = countryArticles.get(firm.countryId);
      if (!current) {
        countryArticles.set(firm.countryId, candidate);
        return;
      }

      if (candidate.totalScore > current.totalScore) { countryArticles.set(firm.countryId, candidate); return; }
      if (candidate.totalScore < current.totalScore) { return; }

      if (candidate.fechaValue > current.fechaValue) { countryArticles.set(firm.countryId, candidate); return; }
      if (candidate.fechaValue < current.fechaValue) { return; }

      if (candidate.arena > current.arena) { countryArticles.set(firm.countryId, candidate); return; }
      if (candidate.arena < current.arena) { return; }

      if (firm.firmName.localeCompare(current.firm.firmName) < 0) {
        countryArticles.set(firm.countryId, candidate);
      }
    });

    return Array.from(countryArticles.values())
      .map(({ firm, article, arena, totalScore, fechaValue }) => ({
        id: article.id,
        link: article.link,
        codigo: article.codigo,
        area: article.area,
        year: article.year,
        fecha: article.fecha,
        title: article.title,
        firmId: article.firmId,
        firmName: article.firmName,
        imageUrl: resolveImageUrl(article, firm),
        fechaValue,
        arena,
        totalScore,
        countryName: firm.countryName,
        // === NUEVO (Paid articles)
        pago: article?.pago ?? false,
        posicion_pago: article?.posicion_pago ?? null,
        fecha_vencimiento: article?.fecha_vencimiento ?? null,
      }))
      .sort((a, b) => {
        if ((b.arena ?? Number.NEGATIVE_INFINITY) !== (a.arena ?? Number.NEGATIVE_INFINITY)) {
          return (b.arena ?? Number.NEGATIVE_INFINITY) - (a.arena ?? Number.NEGATIVE_INFINITY);
        }
        if ((b.totalScore ?? Number.NEGATIVE_INFINITY) !== (a.totalScore ?? Number.NEGATIVE_INFINITY)) {
          return (b.totalScore ?? Number.NEGATIVE_INFINITY) - (a.totalScore ?? Number.NEGATIVE_INFINITY);
        }
        if ((b.fechaValue ?? Number.NEGATIVE_INFINITY) !== (a.fechaValue ?? Number.NEGATIVE_INFINITY)) {
          return (b.fechaValue ?? Number.NEGATIVE_INFINITY) - (a.fechaValue ?? Number.NEGATIVE_INFINITY);
        }
        return (a.countryName ?? '').localeCompare(b.countryName ?? '');
      });
  }, [normalizedFirms, selectedCountry, searchTerm]);

  // ✅ NUEVO: Paid Top 3 (la lógica vive acá; el render se controla abajo con selectedCountry)
  const { paidArticles, activePaidKeys } = useMemo(() => {
    const now = new Date();

    const getArticleKey = (article) => `${article?.id ?? 'unknown'}-${article?.firmId ?? 'firm'}`;

    const isActivePaid = (article) => {
      if (!parsePagoFlag(article?.pago)) return false;
      const pos = parsePosicionPago(article?.posicion_pago);
      if (!pos) return false;;

      const venc = parseDate(article?.fecha_vencimiento);
      if (!venc) return false;
      return venc.getTime() >= now.getTime();
    };

    const orderedPaid = (filteredArticles ?? [])
      .filter(isActivePaid)
      .sort((a, b) => {
        const posDiff = Number(a.posicion_pago) - Number(b.posicion_pago);
        if (posDiff !== 0) return posDiff;
        return (b.fechaValue ?? Number.NEGATIVE_INFINITY) - (a.fechaValue ?? Number.NEGATIVE_INFINITY);
      });

    const byPosition = new Map();
    orderedPaid.forEach((article) => {
      if (!byPosition.has(article.posicion_pago)) {
        byPosition.set(article.posicion_pago, article);
      }
    });

    const topPaid = [1, 2, 3]
      .map((pos) => byPosition.get(pos))
      .filter(Boolean);

    return {
      paidArticles: topPaid,
      activePaidKeys: new Set(orderedPaid.map((article) => getArticleKey(article))),
    };
  }, [filteredArticles]);


  const filteredLegacyArticles = useMemo(() => {
    if (!selectedCountry || activePaidKeys.size === 0) return filteredArticles;
    const getArticleKey = (article) => `${article?.id ?? 'unknown'}-${article?.firmId ?? 'firm'}`;
    return filteredArticles.filter((article) => !activePaidKeys.has(getArticleKey(article)));
  }, [filteredArticles, selectedCountry, activePaidKeys]);


  // Igualar altura sidebar
  useEffect(() => {
    if (!isDesktop) return;
    const el = tableRef.current;
    if (!el) return;

    const update = () => {
      const h = el.getBoundingClientRect().height;
      setSidebarHeight(h);
    };

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener('resize', update);
    update();

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [isDesktop, currentFirms.length, currentPage, sortBy, filteredFirms.length]);

  useEffect(() => {
    setArticleStartIndex((prev) => Math.min(prev, Math.max(0, filteredArticles.length - compactCardsPerView)));
  }, [compactCardsPerView, filteredArticles.length]);

  const maxCarouselIndex = Math.max(0, filteredArticles.length - compactCardsPerView);
  const visibleCarouselArticles = filteredArticles.slice(
    articleStartIndex,
    articleStartIndex + compactCardsPerView
  );
  const shouldCenterCarousel = visibleCarouselArticles.length < compactCardsPerView;

  const handleCarouselPrev = () => setArticleStartIndex((p) => Math.max(0, p - 1));
  const handleCarouselNext = () => setArticleStartIndex((p) => Math.min(maxCarouselIndex, p + 1));
  const handlePageChange = (e, value) => {
    setCurrentPage(value);
    saveFilters(searchTerm, selectedCountry, selectedRegion, value);
  };

  const renderContactButton = (additionalSx = {}) => (
    <Button
      component="a"
      href="https://thelegalindustry.com/contact-us/"
      target="_blank"
      rel="noopener noreferrer"
      variant="contained"
      sx={{
        backgroundColor: '#3592D2',
        color: '#fff',
        borderRadius: { xs: 2.5, sm: 3, md: 4 },
        height: 30,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: '#2878A8' },
        ...additionalSx,
      }}
    >
      Contact us
    </Button>
  );
  // === Tarjeta de artículo
  const renderArticleCard = (article, variant = 'vertical') => (
    <ArticleCard
      key={`${article.id}-${article.firmId}-${variant}`}
      article={article}
      variant={variant}
      compactCardsPerView={compactCardsPerView}
    />
  );


  if (isLoading) return <Loading />;

  // === Sección de filtros
  const FiltersSection = (
    <Container
      sx={{
        mt: { xs: '15px', sm: '30px' },
        mb: { xs: 2, sm: 4 },
        width: sectionWidth,
        maxWidth: `${CONTENT_MAX_PX}px`,
        px: { xs: 0, sm: 2, md: 3 },
      }}
    >
      <Grid container spacing={2} alignItems="center" sx={{ justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Search firm..."
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              width: '100%',
              padding: '8px 0',
              '& .MuiOutlinedInput-root': {
                height: '33px',
                '& fieldset': { borderColor: 'white', borderRadius: '18px' },
                '&:hover fieldset': { borderColor: 'white', borderRadius: '18px' },
                '&.Mui-focused fieldset': { borderColor: '#F67147', borderRadius: '18px' },
              },
              '& .MuiInputLabel-root': { color: 'white', fontSize: '0.8rem' },
              '& .MuiInputBase-input': { color: 'white' },
            }}
            InputLabelProps={{ style: { color: 'white' } }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <SelectOptionMM
            options={uniqueRegions.length > 0 ? uniqueRegions : [{ id: '', name: 'No options' }]}
            onSelectionChange={handleRegionChange}
            name="Filter by Region"
            width="100%"
            selectedOption={selectedRegion}
            mmcolor="white"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <SelectOptionMM
            options={uniqueCountries.length > 0 ? uniqueCountries : [{ id: '', name: 'No options' }]}
            onSelectionChange={handleCountryChange}
            name="Filter by Country"
            width="100%"
            selectedOption={selectedCountry}
            mmcolor="white"
          />
        </Grid>
      </Grid>
    </Container>
  );

  return (
    <ThemeProvider theme={theme}>
      {/* HERO */}
      <Box
        sx={{
          backgroundImage: `url(${buildingsBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          p: { xs: 3, md: 10 },
          textAlign: 'start',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: isTablet ? '2%' : '4%', left: '4%' }}>
          <InstitutionButton institution="legal" />
        </Box>

        <Grid
          item
          xs={12}
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={MMLogo}
            alt="Market Monitor"
            sx={{
              width: isMobile ? '35%' : '20%',
              height: isMobile ? '35%' : '20%',
              marginLeft: isMobile ? '5%' : '2%',
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              mt: isMobile ? 1 : 3,
              textAlign: 'center',
              lineHeight: 1.2,
              fontSize: isMobile ? '0.65rem' : '1.2rem',
              width: '70%',
            }}
          >
            Monitor the real-time{' '}
            <span style={{ fontSize: '115%' }}>positioning</span> of firms in
            your Country and compare it with their global{' '}
            <span style={{ fontSize: '115%' }}>competition</span>
          </Typography>
          <Box
            sx={{
              mt: isMobile ? 1.5 : 3,
              display: 'flex',
              justifyContent: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {/*<Button
              component={RouterLink}
              to="/login"
              onClick={handleSubmitBackgroundClick}
              variant="contained"
              sx={{
                backgroundColor: '#3592D2',
                color: '#fff',
                borderRadius: { xs: 2.5, sm: 3, md: 4 },
                height: 32,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: '#2878A8' },
              }}
            >
              Submit background
            </Button>*/}
          </Box>
        </Grid>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          backgroundColor: '#03263D',
          display: 'flex',
          flexDirection: 'column',
          color: 'white',
          py: isMobile ? 2 : 4,
          pb: '75px',
          overflow: 'hidden',
          px: { xs: 1.25, sm: 0 },
        }}
      >
        {/* Copia + CTA */}
        <Container
          sx={{
            width: sectionWidth,
            maxWidth: `${CONTENT_MAX_PX}px`,
            mx: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            mb: '2%',
            px: { xs: 0, sm: 2, md: 3 },
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              sx={{
                fontSize: isMobile ? '0.6rem' : '0.9rem',
                textAlign: isMobile ? 'center' : 'left',
                mb: isMobile ? 1 : 0,
              }}
            >
              Our AI model is fed with information coming from public sources or provided
              by the firms (verified by our team). Do you want to register a new firm or
              update displayed information?
            </Typography>
          </Box>
          {!showCompactCarousel && (
            <Box sx={{ mt: 0.5, textAlign: { xs: 'center', md: 'left' } }}>
              {renderContactButton()}
            </Box>
          )}
        </Container>

        {/* DESKTOP */}
        {!showCompactCarousel && (
          <>
            <Divider
              sx={{
                borderColor: 'white',
                borderWidth: 1,
                mt: '1%',
                mb: '0.2%',
                width: '80%',
                alignSelf: 'center',
              }}
            />
            {FiltersSection}
          </>
        )}

        {/* MOBILE */}
        {showCompactCarousel && (
          <>
            <Container
              sx={{
                width: sectionWidth,
                maxWidth: `${CONTENT_MAX_PX}px`,
                textAlign: 'center',
                mb: 1,
                px: { xs: 0, sm: 2, md: 3 },
              }}
            >
              {renderContactButton({ height: 34 })}
            </Container>

            <Divider
              sx={{
                borderColor: 'white',
                borderWidth: 1,
                mt: 1,
                mb: 2,
                width: '92%',
                mx: 'auto',
              }}
            />
            {FiltersSection}
          </>
        )}

        {/* Tabla + Sidebar */}
        <Container
          sx={{
            width: sectionWidth,
            maxWidth: `${CONTENT_MAX_PX}px`,
            px: { xs: 0, sm: 1, md: 2 },
          }}
        >
          <Grid
            container
            columnSpacing={2}
            rowSpacing={3}
            alignItems="stretch"
            wrap="nowrap"
          >
            {/* TABLA */}
            <Grid
              item
              xs={12}
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              <Box ref={tableRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FirmsTable firms={currentFirms} sortBy={sortBy} setSortBy={setSortBy} />
                <Pagination
                  count={Math.ceil(filteredFirms.length / firmsPerPage)}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  sx={{
                    mt: 0,
                    justifyContent: 'center',
                    display: 'flex',
                    '& .MuiPaginationItem-root': { color: 'white' },
                  }}
                />
              </Box>
            </Grid>
            {/* SIDEBAR (solo desktop) */}
            {isDesktop && (
              <Grid
                item
                sx={{
                  flex: '0 0 258px',
                  maxWidth: '258px',
                  width: '258px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'transparent',
                    borderRadius: 2,
                    width: '258px',
                    height: sidebarHeight
                      ? `${Math.max(300, Math.min(sidebarHeight, 2150))}px`
                      : '100%',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '258px',
                      height: '100%',
                      minHeight: 0,
                    }}
                  >
                    {/* === NUEVO: sección de artículos pagados (SOLO si hay país seleccionado) */}
                    {selectedCountry && (
                      <>
                        <Box sx={{ px: 0, pt: 0.5 }}>
                          {paidArticles.length === 0 ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.75)',
                                textAlign: 'center',
                                width: '100%',
                                py: 1.5,
                              }}
                            >
                              {/* Paga para subir tu artículo */}
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {paidArticles.map((a) => renderArticleCard(a))}
                            </Box>
                          )}
                        </Box>

                        {/* Barra divisoria ploma entre sección nueva y sección vieja */}
                        <Divider
                          sx={{
                            my: 2,
                            borderColor: 'rgba(160, 160, 160, 0.65)',
                            borderWidth: 1,
                          }}
                        />
                      </>
                    )}

                    {/* === SECCIÓN VIEJA (no modificar): queda abajo y con scroll propio */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        overflowY: 'auto',
                        pr: 1,
                        alignItems: 'flex-start',
                        scrollbarGutter: 'stable',
                        width: '258px',
                        flex: 1,
                        minHeight: 0,
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '10px',
                        },
                      }}
                    >
                      {filteredLegacyArticles.length === 0 ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            textAlign: 'center',
                            width: '100%',
                            py: 2,
                          }}
                        >
                          No articles found.
                        </Typography>
                      ) : (
                        filteredLegacyArticles.map((a) => renderArticleCard(a))
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
        {showCompactCarousel && (
          <Container
            sx={{
              width: sectionWidth,
              maxWidth: `${CONTENT_MAX_PX}px`,
              mt: { xs: 2, sm: 3 },
              mb: { xs: 1.5, sm: 3 },
              px: { xs: 0, sm: 2, md: 3 },
            }}
          >
            <Divider
              sx={{
                borderColor: 'white',
                borderWidth: 1,
                mb: { xs: 2, sm: 3 },
                width: '92%',
                mx: 'auto',
              }}
            />

            {filteredArticles.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textAlign: 'center',
                  width: '100%',
                  py: 2,
                }}
              >
                No articles found.
              </Typography>
            ) : (
              isNarrow ? (
                // NARROW (<=1010px): flechas absolutas
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconButton
                    onClick={handleCarouselPrev}
                    disabled={articleStartIndex === 0}
                    sx={{
                      position: 'absolute', left: { xs: 6, sm: 8 }, top: '50%', transform: 'translateY(-50%)',
                      zIndex: 3, color: 'white',
                      p: { xs: 0.5, sm: 0.75 },
                      border: '1px solid rgba(255,255,255,0.35)',
                      backgroundColor: 'rgba(3,38,61,0.10)',
                      backdropFilter: 'blur(1.5px)',
                      '&:hover': { backgroundColor: 'rgba(3,38,61,0.18)' },
                      '&.Mui-disabled': { opacity: 0.3, borderColor: 'rgba(255,255,255,0.2)' },
                      '& .MuiSvgIcon-root': { fontSize: { xs: '1.25rem', sm: '1.4rem' } },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 1.5, sm: 2 },
                      overflow: 'hidden',
                      width: '100%',
                      justifyContent: shouldCenterCarousel ? 'center' : 'space-between',
                      alignItems: 'center',
                      px: { xs: 2, sm: 0 },
                    }}
                  >
                    {visibleCarouselArticles.map((a) => renderArticleCard(a, 'compact'))}
                  </Box>

                  <IconButton
                    onClick={handleCarouselNext}
                    disabled={articleStartIndex >= maxCarouselIndex}
                    sx={{
                      position: 'absolute', right: { xs: 6, sm: 8 }, top: '50%', transform: 'translateY(-50%)',
                      zIndex: 3, color: 'white',
                      p: { xs: 0.5, sm: 0.75 },
                      border: '1px solid rgba(255,255,255,0.35)',
                      backgroundColor: 'rgba(3,38,61,0.10)',
                      backdropFilter: 'blur(1.5px)',
                      '&:hover': { backgroundColor: 'rgba(3,38,61,0.18)' },
                      '&.Mui-disabled': { opacity: 0.3, borderColor: 'rgba(255,255,255,0.2)' },
                      '& .MuiSvgIcon-root': { fontSize: { xs: '1.25rem', sm: '1.4rem' } },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              ) : (
                // WIDE (>=1011px): flechas dentro del flujo
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                  <IconButton
                    onClick={handleCarouselPrev}
                    disabled={articleStartIndex === 0}
                    sx={{
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.35)',
                      '&.Mui-disabled': { opacity: 0.3, borderColor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 1.5, sm: 2 },
                      overflow: 'hidden',
                      width: '100%',
                      justifyContent: shouldCenterCarousel ? 'center' : 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    {visibleCarouselArticles.map((a) => renderArticleCard(a, 'compact'))}
                  </Box>

                  <IconButton
                    onClick={handleCarouselNext}
                    disabled={articleStartIndex >= maxCarouselIndex}
                    sx={{
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.35)',
                      '&.Mui-disabled': { opacity: 0.3, borderColor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              )
            )}
          </Container>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default MarketMonitorLegal;